import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Plus, Trash2, ShoppingCart, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-contained syndicator cart dialog.
 * Fetches investors + existing syndications internally, then lets user batch-add syndicators.
 *
 * Props:
 *   open         – boolean controlling dialog visibility
 *   onOpenChange – (open: boolean) => void
 *   dealId       – the deal UUID to add syndications to
 *   dealLabel    – optional display label (e.g. "Deal #1234 – Acme Corp")
 *   getAuthHeaders – () => { Authorization: ... }
 *   onSuccess    – callback after successful checkout (used to refresh parent)
 */
const SyndicationCartDialog = ({ open, onOpenChange, dealId, dealLabel, getAuthHeaders, onSuccess }) => {
  const [investors, setInvestors] = useState([]);
  const [syndications, setSyndications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartForm, setCartForm] = useState({ syndicator_id: '', ownership_percentage: '', collect_fees: true });
  const [saving, setSaving] = useState(false);

  // Fetch investors + existing syndications when dialog opens
  const fetchData = useCallback(async () => {
    if (!dealId || !open) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [contactsRes, usersRes, syndRes] = await Promise.all([
        axios.get(`${API}/contacts?type=Investor`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/syndications?deal_id=${dealId}`, { headers }),
      ]);

      // Build investors list (contacts + syndicator users)
      const investorContacts = (contactsRes.data || []).map(c => ({
        id: c.id,
        company_name: c.company_name,
      }));
      const syndicatorUsers = (usersRes.data || [])
        .filter(u => u.roles?.includes('syndicator'))
        .map(u => ({ id: u.id, company_name: `${u.name} (User)` }));
      const combined = [...investorContacts];
      syndicatorUsers.forEach(su => {
        if (!combined.find(c => c.id === su.id)) combined.push(su);
      });

      setInvestors(combined);
      setSyndications(syndRes.data || []);
    } catch {
      toast.error('Failed to load investor data');
    } finally {
      setLoading(false);
    }
  }, [dealId, open, getAuthHeaders]);

  useEffect(() => {
    if (open) {
      fetchData();
      setCart([]);
      setCartForm({ syndicator_id: '', ownership_percentage: '', collect_fees: true });
    }
  }, [open, fetchData]);

  // Allocation math
  const existingPct = useMemo(() =>
    (syndications || []).reduce((sum, s) => sum + (parseFloat(s.ownership_percentage) || 0), 0),
  [syndications]);

  const cartPct = useMemo(() =>
    cart.reduce((sum, item) => sum + (parseFloat(item.ownership_percentage) || 0), 0),
  [cart]);

  const totalPct = existingPct + cartPct;
  const remaining = Math.max(0, 100 - totalPct);

  // Investors already assigned or in cart
  const existingIds = useMemo(() => {
    const ids = new Set((syndications || []).map(s => s.syndicator_id));
    cart.forEach(c => ids.add(c.syndicator_id));
    return ids;
  }, [syndications, cart]);

  const availableInvestors = investors.filter(i => !existingIds.has(i.id));

  const investorName = (id) => investors.find(i => i.id === id)?.company_name || id;

  const handleAddToCart = () => {
    if (!cartForm.syndicator_id || !cartForm.ownership_percentage) {
      toast.error('Select an investor and enter %');
      return;
    }
    const pct = parseFloat(cartForm.ownership_percentage);
    if (pct <= 0 || pct > 100) { toast.error('% must be between 0 and 100'); return; }
    if (totalPct + pct > 100) { toast.error(`Only ${remaining.toFixed(2)}% remaining`); return; }
    setCart([...cart, { ...cartForm, ownership_percentage: pct }]);
    setCartForm({ syndicator_id: '', ownership_percentage: '', collect_fees: true });
  };

  const handleRemoveFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    let added = 0;
    try {
      for (const item of cart) {
        await axios.post(`${API}/syndications`, {
          deal_id: dealId,
          syndicator_id: item.syndicator_id,
          ownership_percentage: item.ownership_percentage,
          collect_fees: item.collect_fees,
        }, { headers: getAuthHeaders() });
        added++;
      }
      toast.success(`${added} syndicator(s) added successfully`);
      setCart([]);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      if (added > 0) toast.success(`${added} syndicator(s) added before error`);
      toast.error(error.response?.data?.detail || 'Failed to add syndicator');
    } finally {
      setSaving(false);
    }
  };

  const pctColor = totalPct === 100 ? 'text-emerald-600' : totalPct > 100 ? 'text-red-600' : 'text-amber-600';
  const barColor = totalPct === 100 ? 'bg-emerald-500' : totalPct > 100 ? 'bg-red-500' : 'bg-blue-500';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="syndication-cart-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" /> Add Syndicators
          </DialogTitle>
          <DialogDescription>
            {dealLabel || 'Select investors and allocate ownership percentages'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-gray-500">Loading investors...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Allocation bar */}
            <div className="space-y-1" data-testid="health-pct-counter">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Allocation</span>
                <span className={`font-bold ${pctColor}`}>{totalPct.toFixed(2)}% / 100%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                {existingPct > 0 && (
                  <div className="h-full bg-gray-300 float-left" style={{ width: `${Math.min(existingPct, 100)}%` }} />
                )}
                <div className={`h-full ${barColor} float-left transition-all`} style={{ width: `${Math.min(cartPct, 100 - existingPct)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400">
                <span>Existing: {existingPct.toFixed(2)}%</span>
                <span>Cart: +{cartPct.toFixed(2)}%</span>
                <span>Remaining: {remaining.toFixed(2)}%</span>
              </div>
            </div>

            {/* Add to cart form */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Investor</Label>
                <Select
                  value={cartForm.syndicator_id}
                  onValueChange={(v) => setCartForm({ ...cartForm, syndicator_id: v })}
                >
                  <SelectTrigger className="h-9" data-testid="health-cart-investor-select">
                    <SelectValue placeholder="Select investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableInvestors.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <Label className="text-xs">%</Label>
                <Input
                  type="number" step="0.01" min="0" max={remaining}
                  value={cartForm.ownership_percentage}
                  onChange={(e) => setCartForm({ ...cartForm, ownership_percentage: e.target.value })}
                  className="h-9"
                  placeholder={`≤${remaining.toFixed(1)}`}
                  data-testid="health-cart-pct-input"
                />
              </div>
              <div className="flex items-center gap-1 pb-0.5">
                <input type="checkbox" id="health_cart_fees" checked={cartForm.collect_fees}
                  onChange={(e) => setCartForm({ ...cartForm, collect_fees: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="health_cart_fees" className="text-xs cursor-pointer whitespace-nowrap">Fees</Label>
              </div>
              <Button size="sm" onClick={handleAddToCart} disabled={!cartForm.syndicator_id || !cartForm.ownership_percentage}
                className="h-9 px-3" data-testid="health-add-to-cart-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Cart items */}
            {cart.length > 0 && (
              <div className="border rounded-lg overflow-hidden" data-testid="health-cart-items">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 font-medium">Investor</th>
                      <th className="text-right p-2 font-medium w-20">%</th>
                      <th className="text-center p-2 font-medium w-16">Fees</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{investorName(item.syndicator_id)}</td>
                        <td className="p-2 text-right font-mono">{item.ownership_percentage}%</td>
                        <td className="p-2 text-center">{item.collect_fees ? <Check className="h-3.5 w-3.5 mx-auto text-green-600" /> : '—'}</td>
                        <td className="p-2">
                          <button onClick={() => handleRemoveFromCart(idx)} className="text-red-400 hover:text-red-600" data-testid={`health-cart-remove-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Checkout */}
            <div className="flex justify-between items-center pt-2 border-t">
              <div className="text-sm">
                <Badge variant="outline" data-testid="health-cart-count">{cart.length} syndicator{cart.length !== 1 ? 's' : ''}</Badge>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">Cancel</Button>
                <Button onClick={handleCheckout} disabled={cart.length === 0 || saving} size="sm" data-testid="health-cart-checkout-btn">
                  {saving ? 'Saving...' : `Save ${cart.length} Syndicator${cart.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SyndicationCartDialog;
